
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { ref, set } from 'firebase/database';
import { Mail, Lock, User, ArrowRight, AlertCircle, Loader2, Phone } from 'lucide-react';

const AuthPage: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        if (password.length < 6) {
          setError('Şifre en az 6 karakter olmalıdır.');
          setLoading(false);
          return;
        }

        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        await updateProfile(user, { displayName: name });
        
        await set(ref(db, `users/${user.uid}`), {
          uid: user.uid,
          email: user.email,
          displayName: name,
          phone: phone,
          isAdmin: false,
          role: 'user',
          createdAt: Date.now()
        });
      }
      navigate('/');
    } catch (err: any) {
      console.error("Auth Error:", err.code);
      switch (err.code) {
        case 'auth/email-already-in-use':
          setError('Bu e-posta adresi zaten kullanımda.');
          break;
        case 'auth/invalid-email':
          setError('Geçersiz bir e-posta adresi girdiniz.');
          break;
        case 'auth/weak-password':
          setError('Şifre çok zayıf (en az 6 karakter).');
          break;
        case 'auth/wrong-password':
        case 'auth/user-not-found':
        case 'auth/invalid-credential':
          setError('E-posta veya şifre hatalı.');
          break;
        default:
          setError('Giriş yapılırken bir hata oluştu.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-12 flex items-center justify-center">
      <div className="w-full max-w-md bg-white rounded-[2.5rem] border border-stone-100 shadow-2xl overflow-hidden p-10 relative">
        <div className="absolute top-0 left-0 w-full h-1.5 bg-stone-900"></div>
        
        <div className="text-center mb-8">
          <h1 className="text-3xl font-black text-stone-900 mb-1 font-serif">
            {isLogin ? 'Hoş Geldiniz' : 'Hesap Oluştur'}
          </h1>
          <p className="text-stone-400 text-xs italic">Lumina Home • Sanatın Işığı</p>
        </div>

        <form onSubmit={handleAuth} className="space-y-5">
          {!isLogin && (
            <>
              <div>
                <label className="block text-[10px] font-black text-stone-400 uppercase tracking-widest mb-1 ml-4">Ad Soyad</label>
                <div className="relative">
                  <User className="absolute left-5 top-1/2 -translate-y-1/2 text-stone-300" size={18} />
                  <input 
                    type="text" required value={name} onChange={e => setName(e.target.value)} 
                    className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 pl-12 pr-6 focus:ring-2 focus:ring-amber-500 outline-none font-bold !text-black placeholder-stone-300" 
                    placeholder="Adınız Soyadınız" 
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-black text-stone-400 uppercase tracking-widest mb-1 ml-4">Telefon</label>
                <div className="relative">
                  <Phone className="absolute left-5 top-1/2 -translate-y-1/2 text-stone-300" size={18} />
                  <input 
                    type="tel" required value={phone} onChange={e => setPhone(e.target.value)} 
                    className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 pl-12 pr-6 focus:ring-2 focus:ring-amber-500 outline-none font-bold !text-black placeholder-stone-300" 
                    placeholder="05XX XXX XX XX" 
                  />
                </div>
              </div>
            </>
          )}

          <div>
            <label className="block text-[10px] font-black text-stone-400 uppercase tracking-widest mb-1 ml-4">E-posta</label>
            <div className="relative">
              <Mail className="absolute left-5 top-1/2 -translate-y-1/2 text-stone-300" size={18} />
              <input 
                type="email" required value={email} onChange={e => setEmail(e.target.value)} 
                className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 pl-12 pr-6 focus:ring-2 focus:ring-amber-500 outline-none font-bold !text-black placeholder-stone-300" 
                placeholder="ornek@mail.com" 
              />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-black text-stone-400 uppercase tracking-widest mb-1 ml-4">Şifre</label>
            <div className="relative">
              <Lock className="absolute left-5 top-1/2 -translate-y-1/2 text-stone-300" size={18} />
              <input 
                type="password" required value={password} onChange={e => setPassword(e.target.value)} 
                className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 pl-12 pr-6 focus:ring-2 focus:ring-amber-500 outline-none font-bold !text-black placeholder-stone-300" 
                placeholder="••••••••" 
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 p-4 rounded-2xl border border-red-100 flex items-center space-x-3">
              <AlertCircle size={18} />
              <span className="text-[10px] font-black uppercase tracking-tight">{error}</span>
            </div>
          )}

          <button 
            type="submit" 
            disabled={loading} 
            className="w-full bg-stone-900 text-white py-5 rounded-2xl font-bold hover:bg-amber-800 transition-all flex items-center justify-center space-x-3 shadow-xl disabled:opacity-50"
          >
            {loading ? <Loader2 size={24} className="animate-spin" /> : (
              <>
                <span>{isLogin ? 'Giriş Yap' : 'Kayıt Ol'}</span>
                <ArrowRight size={20} />
              </>
            )}
          </button>
        </form>

        <div className="mt-8 pt-6 border-t border-stone-50 text-center">
          <button 
            onClick={() => { setIsLogin(!isLogin); setError(''); }} 
            className="text-xs font-bold text-stone-400 hover:text-stone-900 transition-colors"
          >
            {isLogin ? 'Hesabınız yok mu? Kaydolun' : 'Zaten üye misiniz? Giriş yapın'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
